import { Component, EventEmitter, inject, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { APPOINTMENT_CONSTANT } from '../../../../constants/appointment-constants';
import { TRANSLATION_TEMPLATES } from '../../../../constants/translation-templates-constants';
import { WIDGET_CONSTANTS } from '../../../../constants/widget-constants';
import { AppointmentServiceCategoryDao, AppointmentServiceDao } from '../../../../db-models/appointment-service-dao';
import { StoresDao } from '../../../../db-models/stores-dao';
import { CustomBookingMessageTemplate } from '../../../../db-models/widget-conf-dao';
import { Partner } from '../../../../models/global';
import { AppointmentState, ConferenceState } from '../../../../models/state.model';
import { AppointmentService } from '../../../../services/appointment.service';
import { HelperService } from '../../../../services/helper.service';
import { WidgetUtilService } from '../../../../services/widget-util.service';
import { LoaderComponent } from '../../../../shared/components/loader/loader.component';
import { CalioCurrencyPipe } from '../../../../shared/pipes/calio-currency.pipe';
import { CategoryFilterPipe } from '../../../../shared/pipes/category-filter.pipe';
import { HourReplacePipe } from '../../../../shared/pipes/hour-replace.pipe';
import { MinuteReplacePipe } from '../../../../shared/pipes/minute-replace.pipe';
import { ReplaceCommaPipe } from '../../../../shared/pipes/replace-comma.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { CwCheckboxRadioButtonComponent } from '../../../common/theme/cw-checkbox-radio-button/cw-checkbox-radio-button.component';
import { CwMultiLangTitleComponent } from '../../../common/theme/cw-multi-lang-title/cw-multi-lang-title.component';
import { CwQuestionMarkComponent } from '../../../common/theme/cw-question-mark/cw-question-mark.component';
import { CwTitleComponent } from '../../../common/theme/cw-title/cw-title.component';
import { isVatAddedOnTop, vatTypeLabelKey } from '../../../../services/vat-calculation.util';

const modules = [FormsModule, TranslateModule, NgbAccordionModule, FontAwesomeModule];
const components = [CwCheckboxRadioButtonComponent, CwMultiLangTitleComponent, CwQuestionMarkComponent, CwTitleComponent, LoaderComponent];
const pipes = [CalioCurrencyPipe, CategoryFilterPipe, HourReplacePipe, MinuteReplacePipe, ReplaceCommaPipe, TranslationPipe];

@Component({
  selector: 'app-appointment-services',
  templateUrl: './appointment-services.component.html',
  styleUrls: ['./appointment-services.component.scss'],
  imports: [...modules, ...components, ...pipes]
})
export class AppointmentServicesComponent implements OnInit {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;

  @Input() widgetTemplates: CustomBookingMessageTemplate[] = [];
  @Input() appointmentServicesByCat: {
    category: string,
    _translations: any,
    is_multi_language: number,
    appointmentServiceCategory: AppointmentServiceCategoryDao,
    aServicesOfCat: AppointmentServiceDao[],
    position: number
  }[];
  @Input() stores: StoresDao[] = [];
  @Input() lang: string;
  @Input() appointmentState: AppointmentState;
  @Input() partner: Partner;
  @Input() conferenceState: ConferenceState;
  @Input() noAppointments: boolean;

  @Output() serviceChangeEvent = new EventEmitter<{ event: Event, service: AppointmentServiceDao }>();
  @Output() disabledServiceTabEvent = new EventEmitter<any>();
  @Output() navigateToEvent = new EventEmitter<any>();
  @Output() shortcutNavigateToEvent = new EventEmitter<any>();

  private translate = inject(TranslateService);
  private widgetUtilService = inject(WidgetUtilService);
  private deviceDetectorService = inject(DeviceDetectorService);
  private appointmentService = inject(AppointmentService);
  private helperService = inject(HelperService);

  protected readonly templateContent = TRANSLATION_TEMPLATES;

  protected cartSupported: boolean;
  protected isMobile: boolean;
  protected activePanels = ['static-0'];
  protected finalRawServices = Array<AppointmentServiceDao>();
  protected appointmentConstant = APPOINTMENT_CONSTANT;
  protected searchServiceQuery: string;
  protected serviceLabelTemplate: CustomBookingMessageTemplate;
  protected showShortcutButton = false;
  protected hasCombiServiceSelected = false;

  ngOnInit(): void {
    this.translate.onLangChange.subscribe(lang => this.lang = lang.lang);
    this.cartSupported = this.widgetUtilService.getWidgetConf().cartSupported;
    this.isMobile = this.deviceDetectorService.isMobile();

    // If setting is enabled and service is selected than show shortcut button
    this.partner.is_widget_service_shortcut_button_enabled === 1 && (this.showShortcutButton = true);

    // Reset appointments list
    this.appointmentService.resetAppointmentCategoryWiseListEvent.subscribe(() => {
      this.setRawServices();
      this.resetAppointmentCategoryWiseListEvent();
    });

    // Filter services by category if categories are preselected
    if (this.appointmentState?.categories?.length) {
      this.appointmentServicesByCat = this.appointmentServicesByCat.filter(category =>
        this.appointmentState.categories.includes(category.appointmentServiceCategory.id)
      );
    }

    // Skip tab if only single service is present based on setting
    this.checkIfOnlyOneAppointmentServiceIsAvailable();

    // Active specified service accordion or first
    if (this.appointmentState.services && this.appointmentState.services.length > 0) {
      let index = 0;
      const activeIds = Array<string>();
      for (const category of this.appointmentServicesByCat) {
        const foundedService = category.aServicesOfCat.find(item => this.appointmentState.services.indexOf(item.id) > -1);
        if (foundedService) {
          activeIds.push(`static-${index}`);
        }
        index++;
      }
      this.activePanels = activeIds;
    }

    // Expand accordion based on setting
    this.expandCollapseCategoriesBasedOnSetting();
  }

  resetAppointmentCategoryWiseListEvent(): void {
    if (this.appointmentService.mappedServiceIds?.length) {
      let finalServicesCategoryWiseList = [];
      this.appointmentService.mappedServiceIds.forEach(service => {
        const foundService = this.finalRawServices.find(finalRawService => finalRawService.id === service);
        if (foundService) {
          if (finalServicesCategoryWiseList[foundService.appointment_service_category.id]) {
            finalServicesCategoryWiseList[foundService.appointment_service_category.id].aServicesOfCat.push(foundService);
          } else {
            finalServicesCategoryWiseList[foundService.appointment_service_category.id] = {
              category: foundService.appointment_service_category.name,
              _translations: foundService.appointment_service_category._translations,
              is_multi_language: foundService.appointment_service_category.is_multi_language,
              appointmentServiceCategory: foundService.appointment_service_category,
              aServicesOfCat: [foundService],
              position: foundService.appointment_service_category.position
            };
          }
        }
      });
      // Sort the nested arrays based on position
      finalServicesCategoryWiseList?.forEach(category => {
        category?.aServicesOfCat?.sort((a, b) => a.position - b.position);
      });

      // resetting an index of finalServicesCategoryWiseList
      finalServicesCategoryWiseList = finalServicesCategoryWiseList.filter(Boolean);

      // Sort categories
      finalServicesCategoryWiseList = this.helperService.sortByNumber(
        finalServicesCategoryWiseList,
        'position'
      );

      // replacing actual list of array with answered qualification bases intersected services
      this.appointmentServicesByCat = finalServicesCategoryWiseList;
      this.expandCollapseCategoriesBasedOnSetting();
      this.appointmentState.categories = [];
    }
  }

  expandCollapseCategoriesBasedOnSetting(): void {
    if (Number(this.partner.widget_appointment_service_category_status) === 2) {
      const activeIds = Array<string>();
      for (let i = 0; i < this.appointmentServicesByCat.length; i++) {
        activeIds.push(`static-${i}`);
      }
      this.activePanels = activeIds;
    } else if (Number(this.partner.widget_appointment_service_category_status) === 1) {
      const activeIds = Array<string>();
      activeIds.push(`static-0`);
      this.activePanels = activeIds;
    } else if (Number(this.partner.widget_appointment_service_category_status) === 0) {
      this.activePanels = [];
    }
  }

  checkIfOnlyOneAppointmentServiceIsAvailable(): void {
    if (
      (this.stores?.length === 1 && Number(this.partner.widget_jumps_to_resources_step_when_single_service) === 1) ||
      this.conferenceState?.uuid

    ) {
      if (this.appointmentServicesByCat?.length === 1) {
        if (this.appointmentServicesByCat[0].aServicesOfCat && this.appointmentServicesByCat[0].aServicesOfCat.length === 1) {
          if (
            this.appointmentState.services &&
            this.appointmentState.services.indexOf(this.appointmentServicesByCat[0].aServicesOfCat[0].id) === -1
          ) {

            const event: any = {
              target: {
                checked: true
              }
            };
            this.onServiceChange(event, this.appointmentServicesByCat[0].aServicesOfCat[0]);
          }

          of(1).pipe(
            delay(500)
          ).subscribe(() => {
            this.disabledServiceTabEvent.emit(true);
            this.navigateTo(this.appointmentConstant.WORKER, true);
          });
        }
      } else {
        this.disabledServiceTabEvent.emit(false);
      }
    } else {
      this.disabledServiceTabEvent.emit(false);
    }
  }

  onServiceChange(event: Event, service: AppointmentServiceDao): void {
    this.hasCombiServiceSelected = (event.target as HTMLInputElement).checked ? service.is_combi : false;
    this.serviceChangeEvent.emit({ event, service });
  }

  setRawServices(): void {
    const servicesList = this.appointmentServicesByCat.map(ac => ac.aServicesOfCat);
    let finalRawServices: AppointmentServiceDao[] = [];
    for (const services of servicesList) {
      finalRawServices = finalRawServices.concat(services);
    }
    this.finalRawServices = finalRawServices;
  }

  navigateTo(page: string, isFirstTime?: boolean): void {
    this.navigateToEvent.next({ page: page, isFirstTime: isFirstTime });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['widgetTemplates'] && changes['widgetTemplates']['currentValue']) {
      if (this.widgetTemplates && this.widgetTemplates.length > 0) {
        this.setupTemplates();
      }
    }
  }

  setupTemplates(): void {
    this.serviceLabelTemplate = this.widgetTemplates.find((template: CustomBookingMessageTemplate) => {
      return (template).identifier === WIDGET_CONSTANTS.WIDGET_LABELS.WIDGET_APPOINTMENT_SERVICE_LABEL;
    });
    if (this.serviceLabelTemplate) {
      this.serviceLabelTemplate.is_multi_language = 1;
    }
  }

  onSearchServiceQueryChanged(): void {
    let values = Object.assign([], this.appointmentServicesByCat);
    if (this.searchServiceQuery && values?.length) {
      values = values.filter(value => {
        const services = value.aServicesOfCat.filter((appointment: AppointmentServiceDao) => {
          const name = appointment.name ? appointment.name.toLowerCase() : undefined;
          const description = appointment.description ? appointment.description.toLowerCase() : undefined;

          if (name && description) {
            return (name.indexOf(this.searchServiceQuery.toLowerCase()) > -1 || description.indexOf(this.searchServiceQuery.toLowerCase()) > -1);
          } else if (name || description) {
            if (name) {
              return name.indexOf(this.searchServiceQuery.toLowerCase()) > -1;
            }

            if (description) {
              return description.indexOf(this.searchServiceQuery.toLowerCase()) > -1;
            }
          } else {
            return false;
          }
        });
        if (services?.length) {
          value.aServicesOfCat = services;
          return true;
        } else {
          return false;
        }
      });
    }

    let activeIds = Array<string>();
    values.forEach((_value, index) => {
      activeIds.push(`static-${index}`);
    });

    activeIds = [...new Set(activeIds)];
    this.activePanels = activeIds;
  }
}
